import { ComponentPropsWithoutRef, ReactNode, forwardRef, useId } from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { useBulkSelectionScope } from './BulkActionsBar';

interface BulkActionButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'onClick'> {
  /** What the action does, already translated. */
  children: ReactNode;
  onClick?: () => void;
  /**
   * This action cannot be undone from the UI — delete, archive, void, reject.
   *
   * It no longer blocks an escalated selection. A destructive action MAY act on
   * every matching row, behind `BulkScopeConfirmDialog`, which names the count
   * and the rows the user cannot see (`ui-list-pages.md` §11). Refusing outright
   * was the wrong answer to the right worry: it made actions vanish from the bar
   * the moment a user escalated, with the reason hidden in a hover tooltip.
   *
   * What this prop now carries is the destructive STYLING plus the requirement,
   * checked by `tools/check_bulk_matching_policy.py`, that an escalatable
   * destructive action routes through that dialog.
   */
  destructive?: boolean;
  /**
   * This action's endpoint accepts the list's **filter set**, so it can act on
   * every matching row rather than only the loaded page.
   *
   * **Opt-in, and that direction is deliberate.** The capability is a fact about
   * a backend request model (`EscalatableBulkRequest`) that no frontend check can
   * derive, so the safe default is the one that cannot silently lie: an
   * undeclared action is refused while the selection is escalated, rather than
   * quietly acting on 25 rows under a bar reading "All 340 matching selected".
   * Forgetting the prop is then visible and harmless instead of a wrong-scope
   * write.
   *
   * It is not left to memory either — `tools/check_bulk_matching_policy.py`
   * requires every `BulkActionButton` under a bar that offers the escalation to
   * declare this prop or `cannotEscalate`, so an omission fails the build rather
   * than reaching a user.
   */
  escalatable?: boolean;
  /**
   * Why this action cannot reach every matching row — already translated, and
   * **shown on screen** rather than only on hover.
   *
   * Use it when the constraint is inherent to the action rather than a missing
   * endpoint arm (merge needs exactly two records; an action that binds one
   * loaded row's state). When an action is merely waiting on a filter-set arm,
   * omit it and take the generic reason: the fix is to build the arm and pass
   * `escalatable`, not to write prose explaining the gap.
   */
  cannotEscalate?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * A bulk action inside a `BulkActionsBar`.
 *
 * Two jobs. It carries the on-primary styling the bar's dark background needs —
 * previously re-typed as the same class string at every call site — and it reads
 * the selection scope from `BulkSelectionScopeContext` so an action that cannot
 * honour an escalated "all matching" selection says so instead of acting on one
 * page of it.
 *
 * When it does refuse, it refuses **visibly**: the reason renders as text beside
 * the disabled button. A hover-only tooltip was the previous answer and it read,
 * to the user who escalated a selection, as the action having disappeared.
 *
 * @example
 * ```tsx
 * <BulkActionsBar selectedCount={n} selectionScope="page" matchingCount={total}
 *   onSelectAllMatching={selectAll} onSelectPageOnly={selectPage}
 *   onClearSelection={clear}>
 *   <BulkActionButton escalatable onClick={handleActivate}>{t('activate')}</BulkActionButton>
 *   <BulkActionButton escalatable destructive onClick={confirmDelete}>{t('delete')}</BulkActionButton>
 *   <BulkActionButton cannotEscalate={t('merge.needsTwo')} onClick={openMerge}>{t('merge')}</BulkActionButton>
 * </BulkActionsBar>
 * ```
 */
export const BulkActionButton = forwardRef<HTMLButtonElement, BulkActionButtonProps>(function BulkActionButton(
  {
    children,
    onClick,
    destructive = false,
    escalatable = false,
    cannotEscalate,
    disabled = false,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const scope = useBulkSelectionScope();
  const blockedByScope = scope === 'matching' && !escalatable;
  // Ties the visible reason to the control for assistive tech. The previous
  // implementation got this from Radix, which wires `aria-describedby` from a
  // tooltip trigger to its content; dropping the tooltip for on-screen text
  // would otherwise have left the reason as orphaned prose a screen-reader user
  // reaches only by reading the whole bar linearly.
  const reasonId = useId();

  const button = (
    <Button
      // Forwarded so this can BE a Radix trigger (a bulk action that opens a
      // dropdown or dialog is still a bulk action, and must not fall back to a
      // raw Button that would escape the scope policy).
      ref={ref}
      variant="outline"
      size="sm"
      onClick={blockedByScope ? undefined : onClick}
      disabled={disabled}
      // `aria-disabled`, not the native attribute, for the scope refusal: a
      // natively-disabled button leaves the tab order entirely, so a keyboard
      // user tabs straight past the action that just became unavailable and
      // never meets the explanation. It stays focusable and announced as
      // unavailable, with the handler withheld above so it cannot fire.
      // `disabled` is still native for an ordinary in-flight disable, which has
      // no reason to read.
      aria-disabled={blockedByScope || undefined}
      aria-describedby={blockedByScope ? reasonId : undefined}
      className={cn(
        'border-primary-foreground/50 text-primary-foreground bg-transparent hover:bg-primary-foreground/20',
        // Match the native disabled affordance, and drop the hover invitation.
        blockedByScope && 'opacity-50 pointer-events-none hover:bg-transparent',
        className
      )}
      {...rest}
    >
      {children}
    </Button>
  );

  if (!blockedByScope) return button;

  // The reason rides beside the button, in the bar's own on-primary text, so a
  // user who has just escalated can read WHY an action stopped being available
  // without hunting for it. `cannotEscalate` names an inherent constraint; the
  // fallback covers the endpoint that has not grown its filter-set arm yet, and
  // says the one thing that gets the action back.
  //
  // `flex-wrap` + `min-w-0` on the pair and `min-w-0` on the text: the bar wraps
  // whole flex ITEMS, and this button-plus-reason is one item, so without them a
  // long reason cannot reflow and pushes the row into horizontal scroll at
  // 375px — the bar's own `flex-wrap` cannot help from the outside.
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
      {button}
      <span
        id={reasonId}
        className="inline-flex min-w-0 items-center gap-1 text-xs text-primary-foreground/80"
      >
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0">{cannotEscalate ?? t('app.actionNeedsPageScope')}</span>
      </span>
    </span>
  );
});
